import {useCallback} from 'react'

const useMessageHandlers = (state, stateSetters, refs, utilities) => {
  const {messageInputRef, autoScrollTimeoutRef, chatBodyScrollContainerRef, autoScrollToMessageTimeoutRef} = refs
  const {
    setDisableSendButton,
    setContainerWidth,
    setPosition,
    setShowDragbar,
    setShowFullScreen,
    setExpandShrinkTooltipOpen,
    setHiddenWarningBanners
  } = stateSetters
  const {showFullScreen} = state
  const {resetSizes, getMinWidth, getMaxWidth} = utilities

  const clearMessageInput = useCallback(() => {
    messageInputRef.current.value = ''
    setDisableSendButton(true)
  }, [messageInputRef, setDisableSendButton])

  const handleAutoScrollToBottom = useCallback(
    (scrollBehavior = 'smooth') => {
      autoScrollTimeoutRef.current = setTimeout(() => {
        if (chatBodyScrollContainerRef.current?.style) {
          chatBodyScrollContainerRef.current.style.scrollBehavior = scrollBehavior
        }
        document.getElementById('conversation-end')?.scrollIntoView(false)
      }, 100)
    },
    [autoScrollTimeoutRef, chatBodyScrollContainerRef]
  )

  const handleExpandShrink = useCallback(() => {
    resetSizes()
    if (showFullScreen) {
      setContainerWidth(getMinWidth())
      setPosition(getMinWidth())
      setShowDragbar(true)
    } else {
      setContainerWidth(getMaxWidth())
      setPosition(getMaxWidth())
      setShowDragbar(false)
    }
    setShowFullScreen((previousShowFullScreen) => !previousShowFullScreen)
    setExpandShrinkTooltipOpen(false)
  }, [
    resetSizes,
    showFullScreen,
    setContainerWidth,
    getMinWidth,
    setPosition,
    setShowDragbar,
    getMaxWidth,
    setShowFullScreen,
    setExpandShrinkTooltipOpen
  ])

  const handleBannerClose = useCallback(() => {
    setHiddenWarningBanners((prevData) => [...prevData, 'warning_banner'])
  }, [setHiddenWarningBanners])

  const handleAutoScrollToMessage = useCallback(
    (msgObj, scrollBehavior = 'smooth') => {
      autoScrollToMessageTimeoutRef.current = setTimeout(() => {
        chatBodyScrollContainerRef.current.style.scrollBehavior = scrollBehavior
        document.getElementById('msg-end-' + msgObj.id).scrollIntoView(false)
      }, 100)
    },
    [autoScrollToMessageTimeoutRef, chatBodyScrollContainerRef]
  )

  return {
    clearMessageInput,
    handleAutoScrollToBottom,
    handleExpandShrink,
    handleBannerClose,
    handleAutoScrollToMessage
  }
}
export default useMessageHandlers

/*
CHATBOT MESSAGE HANDLERS HOOK

KEY FUNCTIONS:
- handleAutoScrollToBottom: Manages automatic scrolling to newest messages
- handleExpandShrink: Controls chat window expand/shrink functionality
- handleBannerClose: Manages warning banner dismissal
- handleAutoScrollToMessage: Scrolls to specific messages in chat

FEATURES:
- Automatic scroll management
- Chat window size controls
- Banner state management
- Message navigation
- Responsive scroll behavior
*/
